import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { readingPlansApi } from '../../services/api';
import './PlanProgress.css';

const PlanProgress = ({ plan, progress, reader, onRefresh }) => {
  const { preferences } = useAuth();
  const competitiveMode = Boolean(preferences?.competitiveMode);
  const [todaysChapters, setTodaysChapters] = useState(null);
  const [, setSchedule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (progress && progress.isActive) {
      loadTodaysChapters();
      loadSchedule();
    }
  }, [progress]);

  const loadTodaysChapters = async () => {
    try {
      setLoading(true);
      const response = await readingPlansApi.getTodaysChapters(reader.id, plan.id);
      setTodaysChapters(response.data);
    } catch (err) {
      console.error('Error loading today\'s chapters:', err);
      setError('Failed to load today\'s chapters');
    } finally {
      setLoading(false);
    }
  };

  const loadSchedule = async () => {
    try {
      const response = await readingPlansApi.getUpcomingSchedule(reader.id, plan.id, 7);
      setSchedule(response.data);
    } catch (err) {
      console.error('Error loading schedule:', err);
    }
  };

  const getProgressPercentage = () => {
    if (!progress) return 0;
    return Math.round((progress.chaptersRead / progress.totalChapters) * 100);
  };

  const getDaysRemaining = () => {
    if (!progress.targetEndDate) return null;
    const endDate = new Date(progress.targetEndDate);
    const today = new Date();
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const getStatusColor = () => {
    if (!progress) return 'neutral';
    if (progress.completedAt) return 'completed';
    if (progress.isActive) return 'active';
    return 'paused';
  };

  const getStatusText = () => {
    if (!progress) return 'Unknown';
    if (progress.completedAt) return 'Completed';
    if (progress.isActive) return 'Active';
    return 'Paused';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  const statusColor = getStatusColor();

  return (
    <div className={`plan-progress ${statusColor}`}>
      <div className="progress-header">
        <div className="progress-title">
          <h4>{plan.name}</h4>
          <span className={`status-badge ${statusColor}`}>
            {getStatusText()}
          </span>
        </div>
        <div className="progress-percentage">
          {getProgressPercentage()}%
        </div>
      </div>

      <div className="progress-bar-container">
        <div className="progress-bar">
          <div 
            className="progress-fill"
            style={{ width: `${getProgressPercentage()}%` }}
          ></div>
        </div>
        <div className="progress-stats">
          <span>{progress.chaptersRead} / {progress.totalChapters} chapters</span>
          {progress.chaptersPerDay && (
            <span>{progress.chaptersPerDay} chapters/day</span>
          )}
        </div>
      </div>

      <div className="progress-details">
        <div className="detail-row">
          <span className="detail-label">Started:</span>
          <span className="detail-value">{formatDate(progress.startedAt)}</span>
        </div>
        {progress.targetEndDate && (
          <div className="detail-row">
            <span className="detail-label">Target end</span>
            <span className="detail-value">{formatDate(progress.targetEndDate)}</span>
          </div>
        )}
        {getDaysRemaining() !== null && (
          <div className="detail-row">
            <span className="detail-label">Days remaining</span>
            <span className={`detail-value ${getDaysRemaining() < 7 ? 'urgent' : ''}`}>
              {getDaysRemaining()} days
            </span>
          </div>
        )}
        {progress.lastReadAt && (
          <div className="detail-row">
            <span className="detail-label">Last read</span>
            <span className="detail-value">{formatDate(progress.lastReadAt)}</span>
          </div>
        )}
        {competitiveMode && progress.pointsEarned > 0 && (
          <div className="detail-row">
            <span className="detail-label">Points earned</span>
            <span className="detail-value points">{progress.pointsEarned}</span>
          </div>
        )}
      </div>

      {progress.isActive && todaysChapters && (
        <div className="todays-chapters">
          <h5>Today's reading ({todaysChapters.schedule.chaptersForToday} chapters)</h5>
          {todaysChapters.todaysChapters.length > 0 ? (
            <div className="chapters-list">
              {todaysChapters.todaysChapters.map((chapter, index) => (
                <div key={index} className="chapter-item">
                  <span className="chapter-reference">
                    {chapter.book} {chapter.chapter}
                  </span>
                  <span className="chapter-title">{chapter.title}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-chapters">No chapters assigned for today</p>
          )}
          
          {todaysChapters.schedule && (
            <div className="schedule-info">
              <div className="schedule-stat">
                <span className="stat-label">Current pace</span>
                <span className="stat-value">{todaysChapters.schedule.chaptersPerDay} chapters/day</span>
              </div>
              {todaysChapters.schedule.daysRemaining && (
                <div className="schedule-stat">
                  <span className="stat-label">Days left</span>
                  <span className="stat-value">{todaysChapters.schedule.daysRemaining}</span>
                </div>
              )}
              <div className="schedule-stat">
                <span className="stat-label">On track</span>
                <span className={`stat-value ${todaysChapters.schedule.isOnTrack ? 'on-track' : 'off-track'}`}>
                  {todaysChapters.schedule.isOnTrack ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {progress.completedAt && (
        <div className="completion-info">
          <div className="completion-badge">
            🎉 Plan completed!
          </div>
          <p>Completed on {formatDate(progress.completedAt)}</p>
          {competitiveMode && progress.pointsEarned > 0 && (
            <p className="points-earned">
              Earned {progress.pointsEarned} points for completion!
            </p>
          )}
        </div>
      )}

      <div className="progress-actions">
        <button 
          className="refresh-button"
          onClick={onRefresh}
          disabled={loading}
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
    </div>
  );
};

export default PlanProgress;
